// import React, { useState, useEffect } from 'react';
// import { Column } from '@ant-design/charts';

// const BarGraphCard = ({ barGraphData }) => {
//   var data = []
//   if (barGraphData.length != 0) {
//     barGraphData.map((item, index) => (
//       data.push({
//         type: 'Live classes scheduled',
//         short_date: item?.short_date ? item?.short_date : item?.month,
//         count: parseInt(item.total)
//       })
//     ))
//   }

//   if (barGraphData.length != 0) {
//     barGraphData.map((item, index) => (
//       data.push({
//         type: 'Completed',
//         short_date: item?.short_date ? item?.short_date : item?.month,
//         count: item.completed
//       })
//     ))
//   }

//   if (barGraphData.length != 0) {
//     barGraphData.map((item, index) => (
//       data.push({
//         type: 'Cancelled',
//         short_date: item?.short_date ? item?.short_date : item?.month,
//         count: parseInt(item.cancelled)
//       })
//     ))
//   }

//   // console.log(data)
//   var config = {
//     data: data,
//     isGroup: true,
//     xField: 'short_date',
//     yField: 'count',
//     seriesField: 'type',
//   };
//   return <Column {...config} />;
// };

// export default BarGraphCard;

import React, { useMemo } from "react";
import { Column } from "@ant-design/charts";
import moment from "moment";
import useChartBox from "./useChartBox";

const BarGraphCard = ({ barGraphData = [], year, view = 'week' }) => {
  // The chart box is measured here rather than left to g2plot, which sizes the
  // canvas once at construction and falls back to a hardcoded 400x400 whenever
  // that one measurement lands on a box the browser has not laid out yet - a
  // 400px-tall graph in a 350px card is the overflow this page showed at times.
  // See useChartBox.js for the full story.
  const { boxRef, onReady, ready } = useChartBox();

  const data = useMemo(() => {
    if (!barGraphData.length) return [];
    // If month view, aggregate counts per month for the selected `year` and
    // ensure we only include items belonging to that year.
    if (view === 'month') {
      const months = Array.from({ length: 12 }, (_, i) => ({
        month: i + 1,
        total: 0,
        ongoing: 0,
        completed: 0,
        cancelled: 0,
      }));

      barGraphData.forEach((item) => {
        const raw = (item?.short_date ?? item?.month ?? item?.date ?? '').toString();
        const yearMatch = raw.match(/\b(20\d{2})\b/);
        const itemYear = yearMatch ? parseInt(yearMatch[1], 10) : (item?.year ? Number(item.year) : null);
        if (itemYear && itemYear !== year) return; // ignore other years

        // resolve month index
        let m = null;
        const asNum = parseInt(item?.month ?? raw, 10);
        if (!isNaN(asNum) && asNum >= 1 && asNum <= 12) m = asNum;
        else {
          const parsedMonth = moment(raw, ['MMM', 'MMMM', 'M', 'MM'], true);
          if (parsedMonth.isValid()) m = parsedMonth.month() + 1;
        }

        if (m) {
          const slot = months[m - 1];
          slot.total += Number(item.total || 0);
          slot.ongoing += Number(item.ongoing || 0);
          slot.completed += Number(item.completed || 0);
          slot.cancelled += Number(item.cancelled || 0);
        }
      });

      // build the flattened chart data (3 records per month)
      return months.flatMap((slot) => {
        const tooltipLabel = moment().month(slot.month - 1).year(year).format('MMMM YYYY');
        const monthLabel = moment().month(slot.month - 1).format('MMM');
        return [
          { date: monthLabel, tooltipLabel, type: 'Scheduled', value: Math.max(0, slot.total - slot.completed - slot.cancelled) },
          { date: monthLabel, tooltipLabel, type: 'Completed', value: slot.completed },
          { date: monthLabel, tooltipLabel, type: 'Cancelled', value: slot.cancelled },
        ];
      });
    }

    // day/week view: include only items for selected `year` (if item specifies a year)
    return barGraphData.flatMap((item) => {
      const raw = (item?.short_date ?? item?.date ?? '').toString();
      const yearMatch = raw.match(/\b(20\d{2})\b/);
      const itemYear = yearMatch ? parseInt(yearMatch[1], 10) : (item?.year ? Number(item.year) : null);
      if (itemYear && itemYear !== year) return [];

      const parsed = moment(raw, [
        'DD MMM YYYY',
        'DD MMM',
        'DD/MM/YYYY',
        'DD/MM',
        'YYYY-MM-DD',
        'YYYY/MM/DD',
      ], true);

      let display = raw;
      let tooltipLabel = raw;

      if (parsed.isValid()) {
        const includesYear = /\b\d{4}\b/.test(raw) || /[A-Za-z]{3,} \d{4}/.test(raw);
        const useParsed = includesYear ? parsed : parsed.clone().year(year);
        display = useParsed.format('DD/MM');
        tooltipLabel = useParsed.format('DD MMM YYYY, ddd');
      } else {
        const parsedWithYear = moment(`${raw} ${year}`, ['DD MMM YYYY', 'DD/MM/YYYY', 'DD MMM'], true);
        if (parsedWithYear.isValid()) {
          display = parsedWithYear.format('DD/MM');
          tooltipLabel = parsedWithYear.format('DD MMM YYYY, ddd');
        }
      }

      return [
        { date: display, tooltipLabel, type: 'Scheduled', value: Math.max(0, Number(item.total || 0) - Number(item.completed || 0) - Number(item.cancelled || 0)) },
        { date: display, tooltipLabel, type: 'Completed', value: Number(item.completed || 0) },
        { date: display, tooltipLabel, type: 'Cancelled', value: Number(item.cancelled || 0) },
      ];
    });
  }, [barGraphData, year, view]);

  // Robust tooltip title formatter: try multiple parse patterns and fall back sensibly.
  const formatTooltipTitle = (title) => {
    if (!title) return "";

    // Try parsing common date/month formats directly
    let m = moment(title, [
      "DD MMM YYYY",
      "DD MMM",
      "DD/MM/YYYY",
      "YYYY-MM-DD",
      "YYYY/MM/DD",
      "MMM YYYY",
      "MMMM YYYY",
      "MMM",
      "MMMM",
      "MM",
      "M",
    ], true);

    // If direct parse failed, append the year and try again
    if (!m.isValid()) {
      m = moment(`${title} ${year}`, [
        "DD MMM YYYY",
        "DD MMM",
        "DD/MM/YYYY",
        "YYYY-MM-DD",
        "YYYY/MM/DD",
        "MMM YYYY",
        "MMMM YYYY",
      ], true);
    }

    // If still invalid, treat numeric title as month index (1-12)
    if (!m.isValid()) {
      const num = parseInt(title, 10);
      if (!isNaN(num) && num >= 1 && num <= 12) {
        // use first day of that month in the given year
        m = moment(`${year}-${String(num).padStart(2, "0")}-01`, "YYYY-MM-DD");
      }
    }

    // Final fallback: if still invalid, just return the raw title
    if (!m.isValid()) return title;

    // If the original title looks like a month (e.g., "Feb" or "Feb 2026" or numeric month "2"),
    // present it as "MMMM YYYY" (e.g., "February 2026"). Otherwise show full date with weekday.
    const monthOnlyRegex = /^\s*([A-Za-z]{3,}|[A-Za-z]{3,}\s+\d{4}|\d{1,2})\s*$/;
    if (monthOnlyRegex.test(title)) {
      // Ensure we have a year — if parsed moment doesn't include year, use provided `year`.
      const hasYear = /\d{4}/.test(title) || m.year() !== 0;
      const useMoment = hasYear ? m : m.clone().year(year);
      return useMoment.format("MMMM YYYY");
    }

    return m.format("DD MMM YYYY, ddd");
  };

  const yAxisMax = useMemo(() => {
    if (!data || !data.length) return 50;
    const highest = Math.max(...data.map(d => Number(d.value) || 0));
    return highest > 50 ? Math.ceil(highest / 10) * 10 : 50;
  }, [data]);

  // Memoised, and depending only on `data`. This object used to be rebuilt on
  // every render of the parent page, and because it carries function props
  // (`color`, `tooltip.customContent`) every one of those rebuilds looked like a
  // changed config to @ant-design/charts, which answers a changed config by
  // re-initialising the chart and repainting the canvas. The Schedule page
  // re-renders on plenty of things that have nothing to do with this graph (a
  // 500ms drawer-presence poll, a 5s subscription poll, the 30s bucket refetch,
  // any redux slice it reads), so the graph sat there visibly redrawing itself
  // over and over. Note the chart already declares `animation: false` - it is
  // meant to be static once painted.
  const config = useMemo(() => ({
    data,
    isGroup: true,
    // The chart's canvas was staying at whatever width it first rendered at
    // and not resizing when its card shrank without an actual window resize
    // event (e.g. toggling the sidebar open/closed) - autoFit attaches a
    // ResizeObserver to the chart's own container so it redraws whenever
    // that container's size actually changes, not just the window's.
    autoFit: true,

    xField: "date",
    yField: "value",
    seriesField: "type",

    /** BAR LOOK (UNCHANGED) */
    columnWidthRatio: 0.5,
    marginRatio: 0.4,
    radius: [6, 6, 0, 0],

    /** COLORS */
    color: ({ type }) => {
      if (type === "Scheduled") return "#5B73D6";
      if (type === "Completed") return "#00A65A";
      if (type === "Cancelled") return "#FF5B6B";
      return "#ccc";
    },

    /** X AXIS */
    xAxis: {
      tickLine: null,
      line: null,
      label: {
        style: {
          fill: "#9E9E9E",
          fontSize: 12,
        },
      },
    },

    /** Y AXIS */
    yAxis: {
      min: 0,
      max: yAxisMax,
      tickCount: 6,
      grid: {
        line: {
          style: {
            stroke: "#D9D9D9",
            lineDash: [4, 4],
          },
        },
      },
      label: {
        style: {
          fill: "#9E9E9E",
        },
        formatter: (v) => (v === 0 || v === '0') ? '0' : `${v} h`,
      },
    },

    /** LEGEND — rendered ourselves below (see LEGEND_ITEMS/JSX) so the swatches
        can actually be rounded; G2's canvas-drawn "square"/custom rect symbol
        wasn't rendering reliably in this chart library version. */
    legend: false,

    /** ✅ FIXED TOOLTIP (MATCH IMAGE EXACTLY) */
    tooltip: {
      shared: true,
      showMarkers: false,
      showCrosshairs: false,

      domStyles: {
        "g2-tooltip": {
          background: "transparent",
          boxShadow: "none",
          padding: 0,
        },
      },

      customContent: (title, items) => {
        if (!items || !items.length) return "<div style='display:none'></div>";

        // Requirement: Hide tooltip completely if all values are 0
        const hasData = items.some(item => Number(item.value) > 0);
        if (!hasData) return "<div style='display:none'></div>";

        // prefer tooltipLabel set on data records; fallback to title
        const firstData = items[0]?.data || {};
        const tooltipTitle = firstData.tooltipLabel || title || '';

        const itemsToDisplay = items.filter(item => Number(item.value) > 0);

        return `
          <div style="background:#ffffff;padding:14px 16px;border-radius:16px;box-shadow:0 10px 30px rgba(0,0,0,0.18);min-width:220px;">
            <div style="font-size:18px;font-weight:500;color:#6F6F6F;margin-bottom:14px;">
              ${tooltipTitle}
            </div>
            ${itemsToDisplay
            .map(
              (item) => `
                <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:10px;font-size:16px;color:#5A5A5A;">
                  <div style="display:flex;align-items:center;gap:12px">
                    <span style="width:12px;height:12px;border-radius:4px;background:${item.color};"></span>
                    <span>${item.name}:</span>
                  </div>
                  <span style="font-weight:500">${String(item.value).padStart(2, '0')}</span>
                </div>
              `
            )
            .join('')}
          </div>
        `;
      },
    },

    /** KEEP BACKGROUND TOWER SHADOW */
    animation: false,
  }), [data]);

  const LEGEND_ITEMS = [
    { label: "Scheduled", color: "#5B73D6" },
    { label: "Completed", color: "#00A65A" },
    { label: "Cancelled", color: "#FF5B6B" },
  ];

  return (
    // Legend stays above the bars, top-right under the "Summary" header. The donut
    // card's legend is pushed to the bottom of its card (see pieChartCard.js), but
    // this one is deliberately not - restore this to move it below the chart and
    // line the two up:
    //   <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
    //     <Column {...config} height={216} />
    //     <div style={{ display: "flex", justifyContent: "flex-end", gap: 16, marginTop: "auto" }}>
    //       ...legend items...
    //     </div>
    //   </div>
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 16, marginBottom: 4 }}>
        {LEGEND_ITEMS.map((item) => (
          <div key={item.label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 8, height: 8, borderRadius: 3, background: item.color, display: "inline-block" }} />
            <span style={{ fontSize: 12, color: "#8C8C8C" }}>{item.label}</span>
          </div>
        ))}
      </div>
      {/* The chart fills whatever height is left under the legend instead of the
          fixed 216px it used to take, so its baseline and x-axis labels reach the
          bottom of the card - level with the donut card's legend line - rather
          than stopping ~20px short of it. That gap depended on the Summary
          header's own content height (the year/month/days pills), so a hardcoded
          height could never line the two cards up reliably; letting it flex does.
          The chart's `autoFit: true` (see config above) is what measures this box,
          and it carries a ResizeObserver, so the canvas follows the box if it
          changes later. Restore this to go back to the fixed-height chart:
          <Column {...config} height={216} /> */}
      <div ref={boxRef} style={{ flex: 1, minHeight: 0 }}>
        {ready ? (
          <Column {...config} onReady={onReady} style={{ width: "100%", height: "100%" }} />
        ) : null}
      </div>
    </div>
  );
};

// React.memo so the Schedule page's unrelated re-renders (see the config comment
// above) stop reaching this component at all: its props are the redux summary
// array plus two primitives, so a shallow compare is exactly the right test for
// "does this graph actually need to redraw".
export default React.memo(BarGraphCard);
